import type { IConversationMessage } from "@next-platform/client-common-core";
import { isRulebookHudSnapshot, useCharacterRulebookView, usePersona } from "@next-platform/client-common-core";
import { CapabilityGate, SubCapabilityKey } from "@next-platform/client-common-core/infra/application/capabilities";
import { IconSize } from "@next-platform/client-common-spicy-ui";
import { formatMeterDelta, MeterType, type PublicMeterView, type RulebookHudMeter, type RulebookPublicView } from "@next-platform/packages-ts-types";
import React, { useMemo } from "react";

import RulebookMeterIcon from "@/components/Rulebook/RulebookMeterIcon";
import Trans from "@/components/Trans/Trans";
import { useMeterColor } from "@/hooks";

import RulebookHudErrorBoundary from "./RulebookHudErrorBoundary";

type RulebookDeltaChipsProps = {
  message: IConversationMessage;
  characterId?: string;
  isChatWithGroup: boolean;
  "data-testid"?: string;
};

type DeltaChipProps = {
  meterId: string;
  delta: Pick<RulebookHudMeter, "delta" | "receipts" | "rejected">;
  meter?: PublicMeterView;
  /** Dice meters: the face rolled for this reply, shown instead of the change. */
  rolledValue?: number;
  "data-testid"?: string;
};

const formatSignedDelta = (delta: number, meter: PublicMeterView | undefined): string => (meter ? formatMeterDelta(meter, delta) : String(delta));

/** Movements that cancelled out (e.g. a keyword gain and an AI loss) still happened; only a lone zero is noise. */
const hasVisibleMovement = (hudMeter: RulebookHudMeter): boolean => hudMeter.delta !== 0 || (hudMeter.receipts?.length ?? 0) > 1;

const deltaColor = (netDelta: number): string => {
  if (netDelta > 0) {
    return "text-green-9 dark:text-green-8";
  }
  return netDelta < 0 ? "text-red-9" : "text-gray-11";
};

const DeltaChipIcon = ({ meter }: { meter?: PublicMeterView }) => {
  const meterColor = useMeterColor(meter?.color);
  return meter ? (
    <RulebookMeterIcon icon={meter.icon} meterType={meter.type} color={meterColor} size={IconSize.XSMALL} data-testid="DeltaChipIcon" />
  ) : null;
};

const DeltaChip = ({ meterId, delta, meter, rolledValue, "data-testid": testId }: DeltaChipProps) => {
  const isRejected = delta.rejected === "insufficient_funds";
  const rolledValues = useMemo(() => ({ value: rolledValue }), [rolledValue]);
  const netDelta = delta.delta ?? 0;
  const receiptsTitle = delta.receipts?.length ? delta.receipts.join(", ") : undefined;

  return (
    <span
      data-testid={testId ?? `RulebookDeltaChip-${meterId}`}
      data-rejected={isRejected ? "true" : undefined}
      title={receiptsTitle}
      className="inline-flex items-center gap-[5px] rounded-full bg-gray-3 px-[9px] py-[3px] text-xs font-medium text-gray-11 dark:bg-gray-5"
    >
      <DeltaChipIcon meter={meter} />
      {rolledValue !== undefined ? (
        <span className="font-semibold text-foreground">
          <Trans i18nKey="chat:rulebook.hud.delta.rolled" defaults="Rolled {{value}}" values={rolledValues} />
        </span>
      ) : null}
      {rolledValue === undefined && isRejected ? (
        <span className="font-semibold text-red-9">
          <Trans i18nKey="chat:rulebook.hud.delta.insufficientFunds" defaults="Not enough" />
        </span>
      ) : null}
      {rolledValue === undefined && (!isRejected || netDelta !== 0) ? (
        <span className={`font-semibold ${deltaColor(netDelta)}`}>{formatSignedDelta(netDelta, meter)}</span>
      ) : null}
      {meter?.label ?? meterId}
    </span>
  );
};

type RulebookDeltaChipsContentProps = {
  message: IConversationMessage;
  rulebook: RulebookPublicView;
  "data-testid"?: string;
};

const RulebookDeltaChipsContent = ({ message, rulebook, "data-testid": testId = "RulebookDeltaChips" }: RulebookDeltaChipsContentProps) => {
  const meta = isRulebookHudSnapshot(message.rulebook) ? message.rulebook : null;

  const chips = useMemo(() => {
    if (!meta) {
      return [];
    }
    const metersById = new Map((rulebook.meters ?? []).map((meter) => [meter.id, meter]));
    return Object.entries(meta.meters ?? {})
      .filter(([meterId, hudMeter]) => {
        const meter = metersById.get(meterId);
        if (!meter || hudMeter.delta === undefined) {
          return false;
        }
        if (meter.type === MeterType.CLOCK && hudMeter.rejected !== "insufficient_funds") {
          return false;
        }
        if (meter.type === MeterType.DICE) {
          return true;
        }
        return hasVisibleMovement(hudMeter) || hudMeter.rejected === "insufficient_funds";
      })
      .map(([meterId, hudMeter]) => {
        const meter = metersById.get(meterId);
        return { meterId, delta: hudMeter, meter, rolledValue: meter?.type === MeterType.DICE ? hudMeter.value : undefined };
      });
  }, [meta, rulebook]);

  if (chips.length === 0) {
    return null;
  }

  return (
    <div data-testid={testId} className="mt-1.5 flex flex-wrap gap-1.5">
      {chips.map(({ meterId, delta, meter, rolledValue }) => (
        <DeltaChip key={meterId} meterId={meterId} delta={delta} meter={meter} rolledValue={rolledValue} />
      ))}
    </div>
  );
};

const RulebookDeltaChips = ({ message, characterId, isChatWithGroup, "data-testid": testId = "RulebookDeltaChips" }: RulebookDeltaChipsProps) => {
  const { selectedPersona } = usePersona();
  const rulebook = useCharacterRulebookView(characterId, message.conversation_id, selectedPersona?.id);

  if (isChatWithGroup || !rulebook) {
    return null;
  }

  return (
    <RulebookHudErrorBoundary resetKey={message.id}>
      <CapabilityGate capability={SubCapabilityKey.RulebookChatHud}>
        <RulebookDeltaChipsContent message={message} rulebook={rulebook} data-testid={testId} />
      </CapabilityGate>
    </RulebookHudErrorBoundary>
  );
};

export default React.memo(RulebookDeltaChips);
